/** Shared types for the Freight library feature (table, redux, service). */
import type { TabSwitchItem } from '../../../Components/CommonTabSwitch/CommonTabSwitch';

/** One freight rate-card row as returned by the API. Descriptor fields hold ids/strings;
 *  rate fields hold numbers. Kept open-ended so cell renderers can read any column. */
export interface FreightRow {
  _id?: string;
  brand?: string;
  subBrand?: string;
  productType?: string;
  category1?: string;
  category2?: string;
  category3?: string;
  category4?: string;
  season?: string;
  month?: string;
  year?: string;
  supplier?: string;
  factory?: string;
  countryOfOrigin?: string;
  portOfLading?: string;
  countryOfDestination?: string;
  portOfLanding?: string;
  currency?: string;
  freightBasis?: string;
  /** Client-only id for an unsaved new row (before the backend assigns `_id`). */
  __tempId?: string;
  /** Set by the list API when a product costing already priced from this row; it is then locked. */
  inUse?: boolean;
  [key: string]: unknown;
}

/** Per-column search values keyed by field name. */
export type FreightColumnFilters = Record<string, string>;

/** Paginated list/filter state persisted in redux. */
export interface FreightFilterValues {
  searchText: string;
  columnFilters: FreightColumnFilters;
  pageIndex: number;
  pageSize: number;
  sortOrder: '' | 'Asc' | 'Desc';
  sortPropertyName: string;
  /** Header filter-panel picks, kept here so they survive a switch to the Duty tab and back. */
  panelFilters?: Record<string, string[] | string>;
}

/** Query params sent to GET /v1/freight/list. */
export interface FreightListParams {
  pageIndex: number;
  pageSize: number;
  searchText?: string | undefined;
  sortOrder?: '' | 'Asc' | 'Desc' | undefined;
  sortPropertyName?: string | undefined;
  columnFilters?: string | undefined;
}

/** Response envelope for the paginated freight list. */
export interface FreightListResponse {
  code: number;
  data: FreightRow[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
}

/** Redux slice shape for the Freight & Duty module. */
export interface FreightDutyState {
  costingModalState: boolean;
  freightListData: FreightRow[];
  freightTotalCount: number;
  filterValues: FreightFilterValues;
  dutyListData: unknown[];
  dutyTotalCount: number;
  /** Params behind the currently loaded duty page, replayed when scrolling for more. */
  dutyListParams: Record<string, unknown>;
}

export const DEFAULT_FREIGHT_PAGE_SIZE = 20;

/**
 * Glyph size for the bottom floating bar on both library tabs, above `FloatyButton`'s own 28px
 * default. Shared by Freight and Duty so the two bars cannot drift apart.
 */
export const FLOATY_ICON_SIZE = 32;

/**
 * Width of every library table's trailing action gutter, so the icons sit in the same place
 * whichever tab is open. Sized for the widest state — 10px pad + five 24px slots + four 4px
 * gaps + 10px pad — and shared by Freight and Duty rather than computed per table.
 */
export const LIBRARY_ACTIONS_COLUMN_WIDTH = 156;

/** The sibling libraries sharing the Freight/Duty landing page. */
export type LibraryTabKey = 'freight' | 'duty';

/** Single route hosting both tabs; the tab itself travels in `?tab=`. */
export const LIBRARY_ROUTE = '/libraries/freight-duty';

const LIBRARY_TAB_KEYS: LibraryTabKey[] = ['freight', 'duty'];

/**
 * Tab order and labels from Figma node `9391:237000` (Tab Pill Container), fed straight to the
 * shared `CommonTabSwitch`. `eventName` is a full i18n key, resolved against the `common`
 * namespace by that component.
 */
export const LIBRARY_TABS: TabSwitchItem[] = [
  { eventKey: 'freight', eventName: 'FRIEGHT_AND_DUTY.FRIEGHT_DUTY_INDEX.FREIGHT_LIBRARY_TAB' },
  { eventKey: 'duty', eventName: 'FRIEGHT_AND_DUTY.FRIEGHT_DUTY_INDEX.DUTY_LIBRARY_TAB' },
];

/**
 * Reads `?tab=` off a query string, falling back to Freight for missing or unknown values.
 * @param search Location search string, e.g. `?tab=duty`.
 */
export function resolveLibraryTab(search: string): LibraryTabKey {
  const requested = new URLSearchParams(search).get('tab');
  return LIBRARY_TAB_KEYS.find((key) => key === requested) ?? 'freight';
}
